import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, CheckCircle, Activity, AlertCircle } from 'lucide-react';
import Header from '../../../../components/ui/Header';
import Footer from '../../../../components/Footer';
import { ayurvedaService } from '../../../../services/ayurvedaService';

const TreatmentAssessment = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Loading and data states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);

  // Quiz states
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [riskScore, setRiskScore] = useState(0);

  const loadAssessmentData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ayurvedaService?.getCompleteTreatmentPage(slug);

      if (response?.data?.centerPage?.assessmentInfo) {
        const assessmentInfo = response?.data?.centerPage?.assessmentInfo;

        // Validate standardized assessment structure
        if (!assessmentInfo?.questions || assessmentInfo?.questions?.length === 0) {
          throw new Error('Assessment questions not configured');
        }

        if (!assessmentInfo?.scoringRanges || assessmentInfo?.scoringRanges?.length === 0) {
          throw new Error('Assessment scoring ranges not configured');
        }

        setAssessmentData({
          title: assessmentInfo?.assessmentTitle || `${response?.data?.title} Risk Assessment`,
          questions: assessmentInfo?.questions,
          scoringRanges: assessmentInfo?.scoringRanges,
          treatmentName: response?.data?.title
        });
      } else {
        throw new Error('Assessment not available for this treatment');
      }
    } catch (err) {
      console.error('Failed to load assessment:', err);
      setError(err?.message || 'Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadAssessmentData();
  }, [loadAssessmentData]);

  const handleAnswer = (option) => {
    const newAnswers = { ...answers, [currentQuestion]: option };
    setAnswers(newAnswers);

    if (currentQuestion < assessmentData?.questions?.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResults(newAnswers);
    }
  };

  const calculateResults = (allAnswers) => {
    let totalScore = 0;

    Object.values(allAnswers)?.forEach(answer => {
      totalScore += answer?.value || 0;
    });

    setRiskScore(totalScore);
    setShowResults(true);
  };

  const getRiskLevel = () => {
    if (!assessmentData?.scoringRanges) return null;

    // Find the matching scoring range based on score
    const matchingRange = assessmentData?.scoringRanges?.find(range =>
      riskScore >= range?.min && riskScore <= range?.max
    );

    if (matchingRange) {
      return {
        level: matchingRange?.level,
        color: getColorClasses(matchingRange?.color),
        message: matchingRange?.message,
        actions: matchingRange?.actions || []
      };
    }

    // Fallback if no range matches
    return {
      level: "Unknown Risk",
      color: { text: "text-gray-600", bg: "bg-gray-50" },
      message: "Unable to determine risk level",
      actions: ["Please consult with our specialists"]
    };
  };

  const getColorClasses = (colorName) => {
    const colorMap = {
      'red': { text: 'text-red-600', bg: 'bg-red-50' },
      'orange': { text: 'text-orange-600', bg: 'bg-orange-50' },
      'yellow': { text: 'text-yellow-600', bg: 'bg-yellow-50' },
      'green': { text: 'text-green-600', bg: 'bg-green-50' },
      'blue': { text: 'text-blue-600', bg: 'bg-blue-50' }
    };

    return colorMap?.[colorName?.toLowerCase()] || { text: 'text-gray-600', bg: 'bg-gray-50' };
  };

  const getMaxScore = () => {
    if (!assessmentData?.questions) return 0;

    return assessmentData?.questions?.reduce((total, question) => {
      const maxOptionValue = Math.max(...(question?.options?.map(opt => opt?.value || 0)));
      return total + maxOptionValue;
    }, 0);
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const resetAssessment = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setRiskScore(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground text-lg">Loading assessment...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !assessmentData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex flex-col justify-center items-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <AlertCircle size={64} className="text-error mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-3 text-foreground">Assessment Not Available</h2>
            <p className="text-muted-foreground mb-6">
              {error || 'This assessment is currently not configured. Please contact support.'}
            </p>
            <button
              onClick={() => navigate(`/treatments/${slug}`)}
              className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90"
            >
              Back to Treatment Page
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / assessmentData?.questions?.length) * 100;
  const maxScore = getMaxScore();
  const riskLevel = getRiskLevel();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-secondary/5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {assessmentData?.title}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {assessmentData?.treatmentName}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/treatments/${slug}`)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {!showResults && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Question {currentQuestion + 1} of {assessmentData?.questions?.length}</span>
                    <span>{Math.round(progress)}% Complete</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      className="bg-primary h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {!showResults ? (
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">
                    {assessmentData?.questions?.[currentQuestion]?.question}
                  </h3>

                  <div className="space-y-3">
                    {assessmentData?.questions?.[currentQuestion]?.options?.map((option, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleAnswer(option)}
                        className="w-full text-left p-4 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-all duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700 font-medium">{option?.text}</span>
                          <ArrowRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </motion.button>
                    ))}
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                    <button
                      onClick={goBack}
                      disabled={currentQuestion === 0}
                      className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Previous
                    </button>
                    <div className="text-sm text-gray-500">
                      Select an option to continue
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <Activity className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      Assessment Complete!
                    </h3>
                    <p className="text-gray-600">
                      Here's your personalized risk analysis
                    </p>
                  </div>

                  {/* Risk Level Card */}
                  {riskLevel && (
                    <div className={`${riskLevel?.color?.bg} p-6 rounded-xl mb-6 border-2 ${riskLevel?.color?.text?.replace('text-', 'border-')}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <AlertCircle className={`w-6 h-6 ${riskLevel?.color?.text} mr-3`} />
                          <h4 className={`text-xl font-bold ${riskLevel?.color?.text}`}>
                            {riskLevel?.level}
                          </h4>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-gray-700">
                            {riskScore}/{maxScore}
                          </div>
                          <div className="text-sm text-gray-600">Total Score</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {riskLevel?.message}
                      </p>
                    </div>
                  )}

                  {/* Recommendations */}
                  {riskLevel?.actions?.length > 0 && (
                    <div className="bg-gray-50 p-6 rounded-xl mb-6">
                      <h5 className="font-semibold text-lg mb-4 flex items-center text-gray-900">
                        <CheckCircle className="w-5 h-5 text-primary mr-2" />
                        Recommended Actions:
                      </h5>
                      <ul className="space-y-3">
                        {riskLevel?.actions?.map((action, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-success mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Score Ranges Explanation */}
                  <div className="bg-blue-50 p-5 rounded-xl mb-6 border border-blue-200">
                    <h5 className="font-semibold mb-3 text-blue-900">Understanding Your Score:</h5>
                    <div className="space-y-2">
                      {assessmentData?.scoringRanges?.map((range, index) => (
                        <div
                          key={index}
                          className={`text-sm flex items-center justify-between p-2 rounded ${riskScore >= range?.min && riskScore <= range?.max
                            ? 'bg-blue-100 font-semibold' : 'text-blue-700'
                            }`}
                        >
                          <span>• {range?.min}-{range?.max}: {range?.level}</span>
                          {riskScore >= range?.min && riskScore <= range?.max && (
                            <span className="text-xs bg-blue-200 px-2 py-1 rounded-full">Your Score</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button
                      onClick={() => navigate('/booking')}
                      className="flex-1 bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      Book Consultation
                    </button>
                    <button
                      onClick={resetAssessment}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      Retake Assessment
                    </button>
                  </div>

                  {/* Disclaimer */}
                  <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                    <p className="text-sm text-yellow-800">
                      <span className="font-semibold">Medical Disclaimer:</span> This assessment is for educational purposes only and does not replace professional medical diagnosis. Please consult with a qualified healthcare provider for proper evaluation and treatment.
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TreatmentAssessment;